import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Package, ArrowRightLeft, Trophy, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SAR = () => <span style={{ fontFamily: 'inherit' }}>﷼</span>;
const fmt = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 });

const PERIOD_OPTIONS = [
  { value: 'daily',     label: 'اليومية' },
  { value: 'weekly',    label: 'الأسبوعية' },
  { value: 'monthly',   label: 'الشهرية' },
  { value: 'quarterly', label: 'الربعية' },
  { value: 'custom',    label: 'نطاق مخصص' },
];

// Two distinguishable colors for product A vs B
const COLOR_A = '#0EA5E9';
const COLOR_B = '#F59E0B';

export default function SuperProductCompare() {
  const [products, setProducts] = useState([]);
  const [period, setPeriod] = useState('weekly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [productA, setProductA] = useState('');
  const [productB, setProductB] = useState('');
  const [rowsA, setRowsA] = useState([]);
  const [rowsB, setRowsB] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/super/products`)
      .then((r) => {
        setProducts(r.data || []);
        if ((r.data || []).length >= 2 && !productA && !productB) {
          setProductA(r.data[0].name);
          setProductB(r.data[1].name);
        }
      })
      .catch(() => toast.error('فشل تحميل قائمة المنتجات'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch ranking for both products whenever any selector changes
  useEffect(() => {
    if (!productA || !productB) return;
    if (period === 'custom' && (!startDate || !endDate)) return;
    setLoading(true);
    const baseParams = { period };
    if (period === 'custom') {
      baseParams.start_date = startDate;
      baseParams.end_date = endDate;
    }
    Promise.all([
      axios.get(`${API_URL}/api/super/analytics/employees-ranking`, { params: { ...baseParams, product_name: productA } }),
      axios.get(`${API_URL}/api/super/analytics/employees-ranking`, { params: { ...baseParams, product_name: productB } }),
    ])
      .then(([a, b]) => {
        setRowsA(a.data?.rows || []);
        setRowsB(b.data?.rows || []);
      })
      .catch(() => toast.error('فشل تحميل المقارنة'))
      .finally(() => setLoading(false));
  }, [productA, productB, period, startDate, endDate]);

  // Combine: union of employees with their qty/amount per product
  const combined = useMemo(() => {
    const map = new Map();
    for (const r of rowsA) {
      map.set(r.id, {
        id: r.id,
        name: r.name,
        branch_name: r.branch_name,
        qtyA: r.transactions || 0,
        amountA: r.achieved || 0,
        qtyB: 0,
        amountB: 0,
      });
    }
    for (const r of rowsB) {
      const existing = map.get(r.id);
      if (existing) {
        existing.qtyB = r.transactions || 0;
        existing.amountB = r.achieved || 0;
      } else {
        map.set(r.id, {
          id: r.id,
          name: r.name,
          branch_name: r.branch_name,
          qtyA: 0,
          amountA: 0,
          qtyB: r.transactions || 0,
          amountB: r.achieved || 0,
        });
      }
    }
    const all = Array.from(map.values()).filter((e) => e.qtyA > 0 || e.qtyB > 0);
    all.sort((x, y) => (y.qtyA + y.qtyB) - (x.qtyA + x.qtyB));
    return all;
  }, [rowsA, rowsB]);

  const totals = useMemo(() => ({
    qtyA:    combined.reduce((s, x) => s + x.qtyA, 0),
    qtyB:    combined.reduce((s, x) => s + x.qtyB, 0),
    amountA: combined.reduce((s, x) => s + x.amountA, 0),
    amountB: combined.reduce((s, x) => s + x.amountB, 0),
  }), [combined]);

  const winner =
    totals.qtyA === totals.qtyB ? 'tie' : (totals.qtyA > totals.qtyB ? 'A' : 'B');

  const chartData = combined.slice(0, 12).map((e) => ({
    name: e.name.length > 12 ? `${e.name.slice(0, 12)}…` : e.name,
    [productA || 'A']: e.qtyA,
    [productB || 'B']: e.qtyB,
  }));

  return (
    <div className="space-y-6" dir="rtl" data-testid="product-compare-page">
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-xl bg-brand_blue/10 flex items-center justify-center">
          <ArrowRightLeft className="h-6 w-6 text-brand_blue" />
        </div>
        <div>
          <h1 className="text-2xl font-black text-foreground">مقارنة المنتجات</h1>
          <p className="text-sm text-foreground/60">قارن منتجين لمعرفة أيّهما أكثر إنتاجية للموظفين عبر كل الفروع.</p>
        </div>
      </div>

      {/* Selectors */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded" style={{ background: COLOR_A }} />
              المنتج A
            </Label>
            <Select value={productA} onValueChange={setProductA}>
              <SelectTrigger data-testid="product-a-select" className="text-right">
                <SelectValue placeholder="اختر المنتج" />
              </SelectTrigger>
              <SelectContent>
                {products.map((p) => (
                  <SelectItem key={`a-${p.master_id || p.id}`} value={p.name}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded" style={{ background: COLOR_B }} />
              المنتج B
            </Label>
            <Select value={productB} onValueChange={setProductB}>
              <SelectTrigger data-testid="product-b-select" className="text-right">
                <SelectValue placeholder="اختر المنتج" />
              </SelectTrigger>
              <SelectContent>
                {products.map((p) => (
                  <SelectItem key={`b-${p.master_id || p.id}`} value={p.name}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>الفترة</Label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="compare-period-select" className="text-right">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {period === 'custom' && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="compare-custom-range">
            <div className="space-y-1.5">
              <Label>من تاريخ</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="compare-start-date"
                className="text-right"
              />
            </div>
            <div className="space-y-1.5">
              <Label>إلى تاريخ</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="compare-end-date"
                className="text-right"
              />
            </div>
          </div>
        )}
      </Card>

      {/* Totals + Winner */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 border-l-4" style={{ borderRightColor: COLOR_A, borderRightWidth: 4 }}>
          <p className="text-xs text-foreground/55 mb-1">المنتج A</p>
          <p className="font-bold text-base mb-2">{productA || '—'}</p>
          <p className="text-3xl font-black font-numbers" style={{ color: COLOR_A }}>{totals.qtyA}</p>
          <p className="text-xs text-foreground/55 mt-1">عملية بيع</p>
          <p className="text-sm font-numbers font-bold mt-2 text-emerald-700">{fmt(totals.amountA)} <SAR /></p>
        </Card>
        <Card className={`p-4 flex flex-col items-center justify-center text-center ${winner === 'tie' ? '' : 'bg-amber-50'}`}>
          {winner === 'tie' ? (
            <>
              <p className="text-xs text-foreground/55 mb-2">النتيجة</p>
              <p className="text-xl font-black">تعادل</p>
            </>
          ) : (
            <>
              <Trophy className="h-8 w-8 text-amber-500 mb-2" />
              <p className="text-xs text-foreground/55 mb-1">الأكثر إنتاجية</p>
              <p className="font-black text-lg">{winner === 'A' ? productA : productB}</p>
              <p className="text-xs text-foreground/55 mt-1 font-numbers">
                بفارق {Math.abs(totals.qtyA - totals.qtyB)} عملية
              </p>
            </>
          )}
        </Card>
        <Card className="p-4" style={{ borderRightColor: COLOR_B, borderRightWidth: 4 }}>
          <p className="text-xs text-foreground/55 mb-1">المنتج B</p>
          <p className="font-bold text-base mb-2">{productB || '—'}</p>
          <p className="text-3xl font-black font-numbers" style={{ color: COLOR_B }}>{totals.qtyB}</p>
          <p className="text-xs text-foreground/55 mt-1">عملية بيع</p>
          <p className="text-sm font-numbers font-bold mt-2 text-emerald-700">{fmt(totals.amountB)} <SAR /></p>
        </Card>
      </div>

      {/* Bar chart */}
      <Card className="p-4">
        <h3 className="font-bold mb-3 flex items-center gap-2">
          <Package className="h-4 w-4 text-brand_blue" />
          مقارنة المبيعات حسب الموظف (أعلى 12)
        </h3>
        {loading ? (
          <div className="h-72 flex items-center justify-center text-foreground/50">جاري التحميل...</div>
        ) : chartData.length === 0 ? (
          <div className="h-40 flex items-center justify-center text-foreground/50 text-sm">لا توجد مبيعات لأي من المنتجين في هذه الفترة</div>
        ) : (
          <ResponsiveContainer width="100%" height={340}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ direction: 'rtl', fontSize: 12 }}
                formatter={(v) => [`${v} عملية`, '']}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey={productA || 'A'} fill={COLOR_A} radius={[4, 4, 0, 0]} />
              <Bar dataKey={productB || 'B'} fill={COLOR_B} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* Detailed table */}
      <Card className="overflow-hidden">
        <div className="p-3 border-b border-border bg-accent/30">
          <h3 className="font-bold text-sm">تفصيل لكل موظف ({combined.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="compare-table">
            <thead className="bg-accent/40">
              <tr>
                <th className="text-right py-2 px-3 font-bold">الموظف</th>
                <th className="text-right py-2 px-3 font-bold">الفرع</th>
                <th className="text-center py-2 px-3 font-bold" style={{ color: COLOR_A }}>{productA || 'A'} — عمليات</th>
                <th className="text-center py-2 px-3 font-bold" style={{ color: COLOR_B }}>{productB || 'B'} — عمليات</th>
                <th className="text-center py-2 px-3 font-bold">الفائز</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {combined.length === 0 ? (
                <tr><td colSpan="5" className="py-10 text-center text-foreground/50">— لا توجد بيانات —</td></tr>
              ) : combined.map((e) => {
                const w = e.qtyA === e.qtyB ? '—' : (e.qtyA > e.qtyB ? 'A' : 'B');
                return (
                  <tr key={e.id} className="hover:bg-accent/30">
                    <td className="py-2 px-3 font-bold">{e.name}</td>
                    <td className="py-2 px-3 text-foreground/70">{e.branch_name || '—'}</td>
                    <td className="py-2 px-3 text-center font-numbers font-bold" style={{ color: COLOR_A }}>{e.qtyA}</td>
                    <td className="py-2 px-3 text-center font-numbers font-bold" style={{ color: COLOR_B }}>{e.qtyB}</td>
                    <td className="py-2 px-3 text-center font-bold">
                      {w === '—'
                        ? <span className="text-foreground/40">تعادل</span>
                        : <span className="px-2 py-0.5 rounded-md text-white text-xs" style={{ background: w === 'A' ? COLOR_A : COLOR_B }}>{w === 'A' ? 'A' : 'B'}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
